import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import {
  Plus,
  Search,
  Building2,
  MapPin,
  Edit,
  Trash2,
  MoreHorizontal,
  FileText,
  X,
  Download
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { societesService } from "@/services";
import type { Societe } from "@/types";

interface SocieteExtended extends Societe {
  personne_contact?: string | null;
  telephone?: string | null;
  email?: string | null;
  site_web?: string | null;
  conditions?: string | null;
  affaire?: string | null;
  agence_placement?: boolean | null;
}

interface FormData {
  nom: string;
  adresse: string;
  npa: string;
  localite: string;
  personne_contact: string;
  telephone: string;
  email: string;
  site_web: string;
  conditions: string;
  affaire: string;
  agence_placement: boolean;
}

export default function Societes() {
  const [societes, setSocietes] = useState<SocieteExtended[]>([]);
  const [filteredSocietes, setFilteredSocietes] = useState<SocieteExtended[]>([]);
  const [selectedSociete, setSelectedSociete] = useState<SocieteExtended | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    nom: "",
    adresse: "",
    npa: "",
    localite: "",
    personne_contact: "",
    telephone: "",
    email: "",
    site_web: "",
    conditions: "",
    affaire: "",
    agence_placement: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadSocietes();
  }, []);

  useEffect(() => {
    filterSocietes();
  }, [societes, searchTerm]);

  const loadSocietes = async () => {
    try {
      const data = await societesService.list();
      setSocietes(data as SocieteExtended[]);
    } catch (error) {
      console.error('Erreur lors du chargement des sociétés:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les sociétés",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterSocietes = () => {
    if (!searchTerm) {
      setFilteredSocietes(societes);
      return;
    }

    const filtered = societes.filter(societe =>
      societe.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      societe.localite?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      societe.npa?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredSocietes(filtered);
  };

  const openEditDialog = (societe?: SocieteExtended) => {
    if (societe) {
      setSelectedSociete(societe);
      setFormData({
        nom: societe.nom,
        adresse: societe.adresse || "",
        npa: societe.npa || "",
        localite: societe.localite || "",
        personne_contact: societe.personne_contact || "",
        telephone: societe.telephone || "",
        email: societe.email || "",
        site_web: societe.site_web || "",
        conditions: societe.conditions || "",
        affaire: societe.affaire || "",
        agence_placement: societe.agence_placement || false,
      });
    } else {
      setSelectedSociete(null);
      setFormData({
        nom: "",
        adresse: "",
        npa: "",
        localite: "",
        personne_contact: "",
        telephone: "",
        email: "",
        site_web: "",
        conditions: "",
        affaire: "",
        agence_placement: false,
      });
    }
    setEditDialogOpen(true);
  };

  const openDetailDialog = (societe: SocieteExtended) => {
    setSelectedSociete(societe);
    setFormData({
      nom: societe.nom,
      adresse: societe.adresse || "",
      npa: societe.npa || "",
      localite: societe.localite || "",
      personne_contact: societe.personne_contact || "",
      telephone: societe.telephone || "",
      email: societe.email || "",
      site_web: societe.site_web || "",
      conditions: societe.conditions || "",
      affaire: societe.affaire || "",
      agence_placement: societe.agence_placement || false,
    });
    setIsEditing(false);
    setDetailDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom de la société est requis",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      if (selectedSociete) {
        await societesService.update(selectedSociete.id, {
          nom: formData.nom,
          adresse: formData.adresse || undefined,
          npa: formData.npa || undefined,
          localite: formData.localite || undefined,
        });

        toast({
          title: "Société mise à jour",
          description: "Les informations ont été sauvegardées",
        });
      } else {
        await societesService.create({
          nom: formData.nom,
          adresse: formData.adresse || undefined,
          npa: formData.npa || undefined,
          localite: formData.localite || undefined,
        });

        toast({
          title: "Société créée",
          description: "La nouvelle société a été ajoutée",
        });
      }

      setEditDialogOpen(false);
      setDetailDialogOpen(false);
      setIsEditing(false);
      loadSocietes();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder la société",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSociete) return;

    try {
      await societesService.delete(selectedSociete.id);

      toast({
        title: "Société supprimée",
        description: "La société a été supprimée avec succès",
      });

      loadSocietes();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la société",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSociete(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement des sociétés...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Sociétés</h1>
            <p className="text-muted-foreground">
              Gérez les entreprises partenaires et candidates
            </p>
          </div>
          <Button onClick={() => openEditDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle société
          </Button>
        </div>

        {/* Recherche */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Recherche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, localité ou NPA..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </CardContent>
        </Card>

        {/* Liste des sociétés */}
        {filteredSocietes.length === 0 ? (
          <Card className="card-shadow">
            <CardContent className="pt-12">
              <div className="text-center py-12">
                <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {societes.length === 0 ? "Aucune société" : "Aucun résultat"}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {societes.length === 0
                    ? "Commencez par ajouter votre première société"
                    : "Essayez d'ajuster votre recherche"
                  }
                </p>
                {societes.length === 0 && (
                  <Button onClick={() => openEditDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une société
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Agences de placement */}
            {filteredSocietes.filter(s => s.agence_placement).length > 0 && (
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle>
                    Agences de placement ({filteredSocietes.filter(s => s.agence_placement).length})
                  </CardTitle>
                  <CardDescription>
                    Agences spécialisées en placement de personnel
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {filteredSocietes.filter(s => s.agence_placement).map((societe) => (
                      <SocieteCard
                        key={societe.id}
                        societe={societe}
                        onDetail={() => openDetailDialog(societe)}
                        onEdit={() => openEditDialog(societe)}
                        onDelete={() => {
                          setSelectedSociete(societe);
                          setDeleteDialogOpen(true);
                        }}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sociétés conseil */}
            {filteredSocietes.filter(s => !s.agence_placement).length > 0 && (
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle>
                    Sociétés conseil ({filteredSocietes.filter(s => !s.agence_placement).length})
                  </CardTitle>
                  <CardDescription>
                    Sociétés de conseil et prestations de services
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {filteredSocietes.filter(s => !s.agence_placement).map((societe) => (
                      <SocieteCard
                        key={societe.id}
                        societe={societe}
                        onDetail={() => openDetailDialog(societe)}
                        onEdit={() => openEditDialog(societe)}
                        onDelete={() => {
                          setSelectedSociete(societe);
                          setDeleteDialogOpen(true);
                        }}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Dialog d'édition */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedSociete ? "Modifier la société" : "Nouvelle société"}
            </DialogTitle>
            <DialogDescription>
              Renseignez les informations de la société
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Section Informations Générales */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Informations Générales
              </h3>
              <div>
                <Label htmlFor="nom" className="text-sm font-medium">
                  Nom de la société *
                </Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) =>
                    setFormData({ ...formData, nom: e.target.value })
                  }
                  placeholder="Nom de la société"
                  required
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Section Adresse */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Adresse
              </h3>
              <div>
                <Label htmlFor="adresse" className="text-sm font-medium">
                  Rue
                </Label>
                <Input
                  id="adresse"
                  value={formData.adresse}
                  onChange={(e) =>
                    setFormData({ ...formData, adresse: e.target.value })
                  }
                  placeholder="Adresse complète"
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="npa" className="text-sm font-medium">
                    NPA
                  </Label>
                  <Input
                    id="npa"
                    value={formData.npa}
                    onChange={(e) =>
                      setFormData({ ...formData, npa: e.target.value })
                    }
                    placeholder="1234"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="localite" className="text-sm font-medium">
                    Localité
                  </Label>
                  <Input
                    id="localite"
                    value={formData.localite}
                    onChange={(e) =>
                      setFormData({ ...formData, localite: e.target.value })
                    }
                    placeholder="Ville"
                    className="mt-1.5"
                  />
                </div>
              </div>
            </div>

            {/* Section Contact */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Contact
              </h3>
              <div>
                <Label htmlFor="personne_contact" className="text-sm font-medium">
                  Personne de contact
                </Label>
                <Input
                  id="personne_contact"
                  value={formData.personne_contact}
                  onChange={(e) =>
                    setFormData({ ...formData, personne_contact: e.target.value })
                  }
                  placeholder="Nom du contact"
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telephone" className="text-sm font-medium">
                    Téléphone
                  </Label>
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) =>
                      setFormData({ ...formData, telephone: e.target.value })
                    }
                    placeholder="+41 00 000 00 00"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="contact@societe.ch"
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="site_web" className="text-sm font-medium">
                  Site Web
                </Label>
                <Input
                  id="site_web"
                  type="url"
                  value={formData.site_web}
                  onChange={(e) =>
                    setFormData({ ...formData, site_web: e.target.value })
                  }
                  placeholder="https://www.example.com"
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Section Informations Complémentaires */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Informations Complémentaires
              </h3>
              <div>
                <Label htmlFor="conditions" className="text-sm font-medium">
                  Conditions
                </Label>
                <Textarea
                  id="conditions"
                  value={formData.conditions}
                  onChange={(e) =>
                    setFormData({ ...formData, conditions: e.target.value })
                  }
                  placeholder="Conditions générales, notes importantes..."
                  rows={4}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="affaire" className="text-sm font-medium">
                  Affaire
                </Label>
                <Textarea
                  id="affaire"
                  value={formData.affaire}
                  onChange={(e) =>
                    setFormData({ ...formData, affaire: e.target.value })
                  }
                  placeholder="Détails de l'affaire..."
                  maxLength={400}
                  rows={3}
                  className="mt-1.5"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.affaire.length}/400 caractères
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="agence_placement"
                  checked={formData.agence_placement}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, agence_placement: checked === true })
                  }
                />
                <Label
                  htmlFor="agence_placement"
                  className="text-sm font-medium cursor-pointer"
                >
                  Agence de placement
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de détail */}
      <Dialog open={detailDialogOpen} onOpenChange={(open) => {
        setDetailDialogOpen(open);
        if (!open) setIsEditing(false);
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl">
                {selectedSociete?.nom}
              </DialogTitle>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
              )}
            </div>
            <DialogDescription>
              {isEditing ? "Modifiez les informations de la société" : "Détails de la société"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Informations générales */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Informations générales
              </h3>

              <div className="space-y-2">
                <Label htmlFor="detail-nom">Nom de la société *</Label>
                {isEditing ? (
                  <Input
                    id="detail-nom"
                    value={formData.nom}
                    onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder="Ex: Entreprise SA"
                  />
                ) : (
                  <p className="text-sm p-3 bg-muted rounded-md">{selectedSociete?.nom || "-"}</p>
                )}
              </div>
            </div>

            {/* Adresse */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Adresse
              </h3>

              <div className="space-y-2">
                <Label htmlFor="detail-adresse">Rue</Label>
                {isEditing ? (
                  <Input
                    id="detail-adresse"
                    value={formData.adresse}
                    onChange={(e) => setFormData(prev => ({ ...prev, adresse: e.target.value }))}
                    placeholder="Ex: Rue de la Paix 15"
                  />
                ) : (
                  <p className="text-sm p-3 bg-muted rounded-md">{selectedSociete?.adresse || "-"}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="detail-npa">NPA</Label>
                  {isEditing ? (
                    <Input
                      id="detail-npa"
                      value={formData.npa}
                      onChange={(e) => setFormData(prev => ({ ...prev, npa: e.target.value }))}
                      placeholder="Ex: 1000"
                    />
                  ) : (
                    <p className="text-sm p-3 bg-muted rounded-md">{selectedSociete?.npa || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="detail-localite">Localité</Label>
                  {isEditing ? (
                    <Input
                      id="detail-localite"
                      value={formData.localite}
                      onChange={(e) => setFormData(prev => ({ ...prev, localite: e.target.value }))}
                      placeholder="Ex: Lausanne"
                    />
                  ) : (
                    <p className="text-sm p-3 bg-muted rounded-md">{selectedSociete?.localite || "-"}</p>
                  )}
                </div>
              </div>
            </div>

            {selectedSociete && (
              <div className="space-y-2">
                <Label>Date de création</Label>
                <p className="text-sm p-3 bg-muted rounded-md">
                  {formatDate(selectedSociete.createdAt)}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (selectedSociete) {
                      setFormData({
                        nom: selectedSociete.nom,
                        adresse: selectedSociete.adresse || "",
                        npa: selectedSociete.npa || "",
                        localite: selectedSociete.localite || "",
                        personne_contact: selectedSociete.personne_contact || "",
                        telephone: selectedSociete.telephone || "",
                        email: selectedSociete.email || "",
                        site_web: selectedSociete.site_web || "",
                        conditions: selectedSociete.conditions || "",
                        affaire: selectedSociete.affaire || "",
                        agence_placement: selectedSociete.agence_placement || false,
                      });
                    }
                    setIsEditing(false);
                  }}
                  disabled={submitting}
                >
                  Annuler
                </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDetailDialogOpen(false);
                    setSelectedSociete(selectedSociete);
                    setDeleteDialogOpen(true);
                  }}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Fermer
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de suppression */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la société</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer la société "{selectedSociete?.nom}" ?
              Cette action supprimera également tous les candidats associés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

// Component for displaying a societe card
interface SocieteCardProps {
  societe: SocieteExtended;
  onDetail: () => void;
  onEdit: () => void;
  onDelete: () => void;
  formatDate: (date: string) => string;
}

function SocieteCard({ societe, onDetail, onEdit, onDelete, formatDate }: SocieteCardProps) {
  return (
    <div
      className="flex items-center justify-between p-4 border border-card-border rounded-lg hover:bg-muted/50 transition-smooth cursor-pointer"
      onClick={onDetail}
    >
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <Building2 className="h-5 w-5 text-primary mt-1" />
            <div>
              <h4 className="font-semibold text-lg">{societe.nom}</h4>
              {(societe.adresse || societe.npa || societe.localite) && (
                <div className="flex items-center text-sm text-muted-foreground mt-1">
                  <MapPin className="h-3 w-3 mr-1" />
                  <span>
                    {societe.adresse && `${societe.adresse}, `}
                    {societe.npa && `${societe.npa} `}
                    {societe.localite}
                  </span>
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-2">
                Ajoutée le {formatDate(societe.createdAt)}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}
